import { useTranslation } from 'react-i18next';
import { MerchantServiceRateCard } from '@/types';
import { formatWithThousandSeparator } from '@/utils';

interface ServiceRateGridProps {
  services: MerchantServiceRateCard[];
}

/**
 * The BOXY-232 rate grid: one cell per SERVICE × DESTINATION SCOPE.
 *
 * The design draws four — Select Baghdad, Save Baghdad, Select provinces, Save
 * provinces — but nothing here is hardcoded to four, to two services, or to
 * those service codes. The grid is built from whatever the API resolved, so a
 * third service, or a merchant priced on only one, renders correctly without a
 * change here. A page that hardcodes its own product catalogue goes stale the
 * first time the catalogue moves.
 *
 * ⭐ Column ORDER follows the design: all Baghdad cells, then all provinces.
 * Reading down a scope is the comparison a merchant actually makes ("what do I
 * pay in Baghdad on each service"), so scope is the outer grouping even though
 * the data arrives grouped by service.
 */
const ServiceRateGrid = ({ services }: ServiceRateGridProps) => {
  const { t } = useTranslation();

  // Every scope present across every service, Baghdad first, catch-all last —
  // derived rather than assumed, so a scope the backend starts returning shows
  // up without a change here.
  const scopes = Array.from(
    new Set(services.flatMap((service) => service.rates.map((rate) => rate.province_code))),
  ).sort((a, b) => {
    if (a === b) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return a.localeCompare(b);
  });

  const scopeLabel = (code: string | null) =>
    code === null
      ? String(t('pricing_rate_other_provinces'))
      : String(t(`pricing_rate_scope_${code.toLowerCase()}`, { defaultValue: code }));

  // The design labels a cell "Select Bgd", not "Boxy Select · Baghdad". The
  // brand prefix is the same on every cell, so it spends a quarter of a narrow
  // column saying nothing that distinguishes it. Trimmed for DISPLAY only —
  // the title still arrives from the service catalogue, so a renamed or added
  // service still labels itself.
  const shortTitle = (title: string) => title.replace(/^Boxy\s+/i, '');

  const cells = scopes.flatMap((scope) =>
    services.map((service) => ({
      key: `${service.service_code}-${scope ?? 'catch-all'}`,
      label: `${shortTitle(service.title)} ${scopeLabel(scope)}`,
      price: service.rates.find((rate) => rate.province_code === scope)?.price_iqd ?? null,
    })),
  );

  if (cells.length === 0) return null;

  return (
    <div
      className='grid gap-3.5 sm:grid-cols-2 lg:grid-cols-4'
      data-testid='service-rate-grid'
      role='list'
    >
      {cells.map((cell) => (
        <div key={cell.key} role='listitem'>
          <p className='m-0 text-[11px] font-bold uppercase tracking-[0.3px] text-sd-muted-foreground'>
            {cell.label}
          </p>
          <p className='m-0 mt-1.5 text-2xl font-extrabold leading-none text-sd-foreground'>
            {/* A dash, never a zero. Zero is a price a merchant could believe. */}
            {cell.price === null ? '—' : formatWithThousandSeparator(cell.price)}
          </p>
          <p className='m-0 text-[11px] text-sd-muted-foreground'>
            {String(t('pricing_rate_per_order_unit'))}
          </p>
        </div>
      ))}
    </div>
  );
};

export default ServiceRateGrid;
